'use client';

import { AlertTriangle, Coins, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type AlertQuoteInput, useAlertQuote } from '@/hooks/use-alerts';

/** Days used for the "≈ M/Monat" hint. */
const DAYS_PER_MONTH = 30;

/**
 * The price of an alert before it is saved: "+N Credits/Tag (≈ M/Monat)".
 *
 * Zero has three different meanings and each gets its own wording: a
 * grandfathered alert is free by Bestandsschutz, an edit that changes nothing
 * costs nothing more, and a company already covered by another alert (a
 * watched list, say) adds nothing because each company is billed once.
 */
export function AlertQuoteLine({
  input,
  exempt = false,
  exemptEnds = false,
  isExisting = false,
}: {
  input: AlertQuoteInput | null;
  /** The alert is grandfathered (`billing_exempt`). */
  exempt?: boolean;
  /** This change would end the grandfathering (e.g. switching to Stündlich / `immediately`). */
  exemptEnds?: boolean;
  /** Editing an existing alert rather than creating one. */
  isExisting?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useAlertQuote(input);
  const nf = new Intl.NumberFormat(
    i18n.language?.startsWith('de') ? 'de-DE' : 'en-US',
  );

  let content: React.ReactNode;
  if (!input) return null;
  if (isLoading) {
    content = (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        {t('alerts.quote.loading')}
      </span>
    );
  } else if (isError || !data) {
    content = (
      <span className="text-muted-foreground">{t('alerts.quote.error')}</span>
    );
  } else {
    const n = data.increment;
    let main: string;
    if (n > 0) {
      main = t('alerts.quote.increment', {
        count: n,
        n: nf.format(n),
        month: nf.format(n * DAYS_PER_MONTH),
      });
    } else if (n < 0) {
      main = t('alerts.quote.decrement', {
        count: -n,
        n: nf.format(-n),
        month: nf.format(-n * DAYS_PER_MONTH),
      });
    } else if (exempt && !exemptEnds) {
      main = t('alerts.quote.freeGrandfathered');
    } else if (isExisting) {
      main = t('alerts.quote.noChange');
    } else if (input.subject_type === 'feed') {
      main = t('alerts.quote.free');
    } else {
      main = t('alerts.quote.freeAlreadyWatched');
    }
    content = (
      <span className="flex flex-col gap-0.5">
        <span className="font-medium text-foreground">{main}</span>
        {typeof data.total_after === 'number' && (
          <span className="text-xs text-muted-foreground">
            {t('alerts.quote.totalAfter', {
              count: data.total_after,
              n: nf.format(data.total_after),
            })}
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
        <Coins className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        {content}
      </div>
      {exemptEnds && (
        <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          {t('alerts.quote.grandfatherEnds')}
        </p>
      )}
    </div>
  );
}
